import { EvidenceType, FeatureTypes, SpanLevel } from '../../enums';
import { FieldMap } from '../evidence2/field-map';
import { isCategorical, isNgram, isPrior } from '../../typeguards';
import { uuidv4 } from './uuidv4';
import { isLinkable } from './artifact-filters';

type UnifiedProcedure = InvoicesAPI.ProcedureCode | AmplifyAPI.ProcedureCode;
type UnifiedDiagnosis = InvoicesAPI.DiagnosticCode | AmplifyAPI.DiagnosisCode;
type UnifiedModifier = InvoicesAPI.Modifier | AmplifyAPI.ModifierCode;

const psuedoCategoricalTypes = new Set<string>([
  'exam_type',
  'raw_reasonforvisit'
]);

const isObject = obj => {
  return obj === Object(obj);
};

const _convertModifierEvidence = (
  medicalCode: UnifiedModifier
): CMxCommonApp.ChargeSessionEvidence[] => {
  return [{ ...mappedProto }];
};

const _convertTechChargeEvidence = (medicalCode: InvoicesAPI.TechnicalCode) => {
  return [{ ...mappedProto }];
};

const mappedProto: CMxCommonApp.ChargeSessionEvidence = {
  linkable: false,
  displayable: true,
  selectionState: {
    active: false,
    hover: false,
    visible: false
  },
  featureType: 'mapping',
  identifier: null,
  fieldName: null,
  fieldIdentifier: null,
  meta: null
};

const clinicalProto: CMxCommonApp.ChargeSessionEvidence = {
  linkable: false,
  displayable: true,
  selectionState: {
    active: false,
    hover: false,
    visible: false
  },
  featureType: 'complete',
  identifier: null,
  fieldName: null,
  fieldIdentifier: null,
  meta: null
};

const evidenceProto: CMxCommonApp.ChargeSessionEvidence = {
  linkable: true,
  displayable: true,
  selectionState: {
    active: false,
    hover: false,
    visible: false
  },
  featureType: 'complete',
  identifier: null,
  fieldName: 'raw_body',
  fieldIdentifier: null,
  meta: null
};

const isSentence = span => span.level === SpanLevel.SENTENCE;

const expandSpans = (
  ret: CMxCommonApp.ChargeSessionEvidence,
  origSpans: CMxAPI.Span[]
): CMxCommonApp.ChargeSessionEvidence[] => {
  // deal with spans
  let spans = origSpans?.filter(isSentence);
  spans = spans.map(span => {
    const fieldName =
      FieldMap[ret.fieldName] ||
      (ret.fieldName as CMxCommonApp.ChargeSessionEvidenceFieldName);
    const { start = '', end = '' } = span;
    // spans won't hash out the same way the fields, they require
    // additional hashing to make sure they're unique.  in this case,
    // there should only be one with a field id and start and end locations
    const hash = `${fieldName}__${ret.fieldIdentifier}__${start}-${end}`;

    return {
      ...span,
      hash,
      identifier: hash
    };
  });

  // each span will have a corresponding 1:1 mapping with a piece of
  // ChargeSessionEvidence
  return spans.map(span => {
    return {
      ...ret,
      meta: span
    };
  });
};

const filterEvidence = (feature: CMxAPI.MLFeature) => {
  if (!feature) return false;

  if (feature.type === FeatureTypes.MAPPING) {
    return true;
  }
  if (isPrior(feature)) {
    return false;
  }
  if (isNgram(feature)) {
    if (psuedoCategoricalTypes.has(feature.field)) {
      return true;
    }
    const spans =
      (feature as CMxAPI.NgramFeature).spans?.filter(isSentence) ?? [];
    if (spans.length === 0) {
      return false;
    }
  }

  return feature !== null;
};

const preprocessFeature = (feature: any) => {
  if (!feature) return;

  if ('feature' in feature) {
    return feature.feature;
  } else if (feature.type === FeatureTypes.MAPPING) {
    return { ...mappedProto };
  } else if ('field' in feature) {
    return feature;
  }
};

const destructureFeature = (feature: CMxAPI.IndividualEvidence) => {
  return feature.feature;
};

const _processFeature = (identifier: string, feature: CMxAPI.MLFeature) => {
  let ret:
    | CMxCommonApp.ChargeSessionEvidence
    | CMxCommonApp.ChargeSessionEvidence[] = {
    ...evidenceProto,
    identifier
  };
  try {
    if (isNgram(feature)) {
      const fieldName =
        FieldMap[feature.field] ||
        (feature.field as CMxCommonApp.ChargeSessionEvidenceFieldName);
      ret.fieldIdentifier = identifier;
      ret.linkable = isLinkable(fieldName);
      // check to see if we should treat it as categorical
      if (psuedoCategoricalTypes.has(feature.field)) {
        //console.log('psuedoe');
        ret.featureType = 'complete';
        ret.fieldName = fieldName;
      } else {
        ret.featureType = 'partial';
        ret.fieldName = fieldName;
        ret = expandSpans(ret, feature.spans);
        ret.forEach(spanFeature => {
          spanFeature.fieldName = fieldName;
        });
      }
    } else if (isCategorical(feature)) {
      ret.featureType = 'complete';
      const fieldName =
        FieldMap[feature.field] ||
        (feature.field as CMxCommonApp.ChargeSessionEvidenceFieldName);
      ret.fieldName = fieldName;
      ret.linkable = isLinkable(fieldName);
      ret.meta = {
        content: feature.category
      };
    } else {
      //console.log('unknown type');
    }
  } catch (e) {
    console.log('exception process feature', feature);
  }

  return ret;
};

const hashFeature = (evidence: CMxCommonApp.ChargeSessionEvidence) => {
  const fieldName =
    FieldMap[evidence.fieldName] ||
    (evidence.fieldName as CMxCommonApp.ChargeSessionEvidenceFieldName);
  if (evidence.featureType === EvidenceType.COMPLETE) {
    return `${fieldName}-${evidence.fieldIdentifier}`;
  } else if (evidence.featureType === EvidenceType.PARTIAL) {
    const { start = '', end = '' } = evidence.meta ?? {};
    return `${fieldName}__${evidence.fieldIdentifier}__${start}-${end}`;
  }
};

const dedupe = (
  evidence: CMxCommonApp.ChargeSessionEvidence[]
): CMxCommonApp.ChargeSessionEvidence[] => {
  const seen = new Set<string>();
  const filtered = [];

  evidence.forEach(piece => {
    const hash = hashFeature(piece);

    if (!seen.has(hash)) {
      piece.hash = hash;
      filtered.push(piece);
    }
    seen.add(hash);
  });
  return filtered;
};

const convertDX = (
  medicalCode: UnifiedDiagnosis,
  identifier: string = '',
  uniqify: boolean = true
) => {
  const features = medicalCode.evidence?.map(destructureFeature) ?? [];
  const evidence = features?.filter(filterEvidence);
  const processedFeatures = evidence
    .map(_processFeature.bind(null, identifier))
    .flat();
  const identifiedFeatures = processedFeatures.map(feature => {
    // identifiers may be dependent on icd codes which contain decimals
    // guarding against colons as well.
    feature.fieldIdentifier = `${identifier}`;
    return feature;
  });
  return uniqify ? dedupe(identifiedFeatures) : identifiedFeatures;
};

const convertCPT = (
  medicalCode: UnifiedProcedure,
  identifier: string = '',
  uniqify: boolean = true
) => {
  const rawEvidence: any[] = Array.isArray(medicalCode.evidence)
    ? [...medicalCode.evidence]
    : isObject(medicalCode.evidence)
    ? [{ ...medicalCode.evidence }]
    : [];

  const preprocessed = rawEvidence.map(preprocessFeature) ?? [];
  const evidence = preprocessed.filter(filterEvidence);
  const processedFeatures = evidence
    .map(_processFeature.bind(null, identifier))
    .flat();
  const identifiedFeatures = processedFeatures.map(feature => {
    // this shouldn't have decimals, but this is a conservative approach
    feature.fieldIdentifier = `${identifier}`;
    return feature;
  });
  return uniqify ? dedupe(identifiedFeatures) : identifiedFeatures;
};

export { convertDX, convertCPT };
